import { useState, useEffect } from 'react'
import { mutationQueue } from '../services/offline/mutationQueue'
import { formPersistence } from '../services/offline/formPersistence'

export interface ProtocolOfflineStatus {
  hasPendingMutations: boolean
  hasFailedMutations: boolean
  hasDrafts: boolean
  pendingCount: number
  failedCount: number
  draftsCount: number
}

export function useProtocolOfflineStatus(protocolId?: number): ProtocolOfflineStatus {
  const [status, setStatus] = useState<ProtocolOfflineStatus>({
    hasPendingMutations: false,
    hasFailedMutations: false,
    hasDrafts: false,
    pendingCount: 0,
    failedCount: 0,
    draftsCount: 0
  })

  useEffect(() => {
    if (!protocolId) {
      setStatus({
        hasPendingMutations: false,
        hasFailedMutations: false,
        hasDrafts: false,
        pendingCount: 0,
        failedCount: 0,
        draftsCount: 0
      })
      return
    }

    const checkProtocolStatus = async () => {
      try {
        // Check mutations related to this protocol
        const mutations = await mutationQueue.getMutationsByProtocol(protocolId)
        const pending = mutations.filter((m) => m.status === 'pending')
        const failed = mutations.filter((m) => m.status === 'failed')

        // Check drafts for this protocol
        const allDrafts = formPersistence.getAllDrafts()
        const protocolDrafts = allDrafts.filter((draft) => draft.protocolId === protocolId)

        setStatus({
          hasPendingMutations: pending.length > 0,
          hasFailedMutations: failed.length > 0,
          hasDrafts: protocolDrafts.length > 0,
          pendingCount: pending.length,
          failedCount: failed.length,
          draftsCount: protocolDrafts.length
        })
      } catch (error) {
        console.error('Failed to check protocol offline status:', error)
        // Reset to safe defaults on error
        setStatus({
          hasPendingMutations: false,
          hasFailedMutations: false,
          hasDrafts: false,
          pendingCount: 0,
          failedCount: 0,
          draftsCount: 0
        })
      }
    }

    checkProtocolStatus()

    // Check every 5 seconds
    const interval = setInterval(checkProtocolStatus, 5000)

    return () => clearInterval(interval)
  }, [protocolId])

  return status
}

export function useAllProtocolsOfflineStatus(protocolIds: number[]) {
  const [statuses, setStatuses] = useState<Map<number, ProtocolOfflineStatus>>(new Map())

  useEffect(() => {
    const checkAllProtocolsStatus = async () => {
      // Return early if no protocol IDs
      if (!protocolIds || protocolIds.length === 0) {
        setStatuses(new Map())
        return
      }

      const newStatuses = new Map<number, ProtocolOfflineStatus>()

      try {
        // Get all mutations and drafts once
        await mutationQueue.init()
        const [pendingMutations, failedMutations] = await Promise.all([
          mutationQueue.getPendingMutations(),
          mutationQueue.getFailedMutations()
        ])
        const allMutations = [...pendingMutations, ...failedMutations]
        const allDrafts = formPersistence.getAllDrafts()

        // Process each protocol
        for (const protocolId of protocolIds) {
          if (!protocolId || isNaN(protocolId)) continue

          const mutations = allMutations.filter((m) => m.protocolId === protocolId)
          const pending = mutations.filter((m) => m.status === 'pending')
          const failed = mutations.filter((m) => m.status === 'failed')
          const protocolDrafts = allDrafts.filter((draft) => draft.protocolId === protocolId)

          newStatuses.set(protocolId, {
            hasPendingMutations: pending.length > 0,
            hasFailedMutations: failed.length > 0,
            hasDrafts: protocolDrafts.length > 0,
            pendingCount: pending.length,
            failedCount: failed.length,
            draftsCount: protocolDrafts.length
          })
        }
      } catch (error) {
        console.error('Failed to check all protocols offline status:', error)
        // Create empty statuses for all protocols on error
        for (const protocolId of protocolIds) {
          if (protocolId && !isNaN(protocolId)) {
            newStatuses.set(protocolId, {
              hasPendingMutations: false,
              hasFailedMutations: false,
              hasDrafts: false,
              pendingCount: 0,
              failedCount: 0,
              draftsCount: 0
            })
          }
        }
      }

      setStatuses(newStatuses)
    }

    if (protocolIds && protocolIds.length > 0) {
      checkAllProtocolsStatus()

      // Check every 5 seconds
      const interval = setInterval(checkAllProtocolsStatus, 5000)
      return () => clearInterval(interval)
    } else {
      setStatuses(new Map())
    }
  }, [JSON.stringify(protocolIds)]) // Use JSON.stringify to properly detect changes in array

  return statuses
}
